/**
 * FindStorePresenter
 * --
 */
import React from 'react';
import { Content, Image, KakaoMap, ModalLayout, Text, Title } from 'components';
import { Button, Carousel, Flex, Input, Spin } from 'antd';
import {
  CaretDownOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  PhoneOutlined,
  SearchOutlined,
  ShopOutlined,
} from '@ant-design/icons';
import './FindStorePresenter.style.css';

const SERVICES = {
  WIFI: '무선 인터넷',
  GROUP: '단체 이용 가능',
  PACKAGING: '주차',
  RESTROOM: '남/녀 화장실 구분',
  WHEELCHAIR: '출입구 휠체어 이용 가능',
};

const FindStorePresenter = ({
  screenSize,
  stores = [],
  onSearch,
  inputValue,
  setInputValue,
  isLoading,
}) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  const [selected, setSelected] = React.useState(null);
  const [collapse, setCollapse] = React.useState(false);

  const [modal, setModal] = React.useState(null);

  /* ===== VARIABLE ===== */
  const styles = {
    button: {
      width: 200,
      padding: '20px 70px',
      borderRadius: 0,
    },
    unactiveButton: {
      backgroundColor: '#ddd',
      color: 'var(--light-gray-color)',
    },
  };

  /* ===== FUNCTION ===== */
  /* ===== HOOKS ===== */
  /**
   * 검색된 스토어중 처음 선택되어 있는 값 설정
   * --
   */
  React.useEffect(() => {
    if (!stores.length) return;

    setSelected(stores?.[0]);
  }, [stores]);

  /* ===== RENDER ===== */
  return (
    <>
      <Spin spinning={isLoading}>
        <Content
          className="findstore"
          maxWidth={'100%'}
          padding={0}
          style={{ position: 'relative', top: 0, left: 0 }}
        >
          <KakaoMap
            width={'100%'}
            height={'100%'}
            name={selected?.shop_information_name}
            address={selected?.shop_information_address}
          />
          <div
            className="search_map"
            style={{
              position: 'absolute',
              top: screenSize >= 520 ? 30 : 0,
              left: screenSize >= 520 ? 30 : 0,
              zIndex: 9,
            }}
          >
            <div className="search">
              <Input
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="매장명 또는 주소"
                onPressEnter={() => onSearch('search')}
                onClear={() => onSearch('clear')}
              />
              <SearchOutlined
                style={{
                  fontSize: '1.8em',
                  color: 'var(--white-color)',
                  cursor: 'pointer',
                }}
                onClick={() => onSearch('search')}
              />
            </div>
            <div
              className="num"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <p>
                검색결과 <strong>{stores?.length}</strong> 개
              </p>

              {screenSize >= 520 ? null : (
                <Button
                  size="small"
                  icon={<CaretDownOutlined />}
                  type="text"
                  onClick={() => setCollapse(!collapse)}
                />
              )}
            </div>
            <ul className={`store_wrap ${collapse ? 'collapsed' : ''}`}>
              {stores?.map((store) => (
                <li
                  key={`store-${store.shop_information_id}`}
                  className={`store_item ${
                    selected &&
                    selected?.shop_information_id === store?.shop_information_id
                      ? 'selected'
                      : ''
                  }`}
                  onClick={() => setSelected(store)}
                >
                  <h3>{store?.shop_information_name}</h3>
                  <p>
                    ({store?.shop_information_postcode}){' '}
                    {store?.shop_information_address}{' '}
                    {store?.shop_information_address_detail ?? ''}
                  </p>
                  <p>{store?.store_num}</p>
                  {/* TODO: 제공 서비스 아이콘 */}
                  <Button className="more" onClick={() => setModal(store)}>
                    상세보기
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        </Content>
      </Spin>

      <ModalLayout
        open={!!modal}
        onCancel={() => setModal(null)}
        type="drawer"
        placement={screenSize >= 500 ? 'left' : 'bottom'}
        width={350}
        height="80%"
        title={
          <Flex align="center" justify="space-between">
            <Title size={5}>매장정보</Title>
            <Button
              size="small"
              icon={<CloseOutlined />}
              type="text"
              onClick={() => setModal(null)}
            />
          </Flex>
        }
      >
        <Content maxWidth={'100%'} padding={0}>
          <Carousel autoplay>
            {modal?.files?.length ? (
              modal?.files?.map((file, index) => (
                <Image
                  key={`file-${index}`}
                  src={file}
                  alt={modal?.shop_information_name}
                  width={'100%'}
                  height={200}
                />
              ))
            ) : (
              <Image
                noImageStyle={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
                alt={modal?.shop_information_name}
                width={'100%'}
                height={200}
              />
            )}
          </Carousel>

          <Text weight={700} style={{ marginTop: 20 }} maxLine={2}>
            {modal?.shop_information_name}
          </Text>

          <div style={{ marginTop: 5 }}>
            <Text size={14} color={'#666666'}>
              {modal?.shop_information_address}
            </Text>
            <Text size={14} color={'#666666'}>
              {modal?.shop_information_address_detail}
            </Text>
          </div>

          <Flex vertical gap={8} style={{ marginTop: 10 }}>
            <Flex gap={8} align="flex-start">
              <ClockCircleOutlined />

              <Text size={12}>{modal?.business_hour ?? '-'}</Text>
            </Flex>

            <Flex gap={8} align="flex-start">
              <PhoneOutlined style={{ transform: 'rotate(90deg)' }} />

              <Text size={12}>{modal?.store_num ?? '-'}</Text>
            </Flex>

            <Flex gap={8} align="flex-start">
              <ShopOutlined />

              <Text size={12} maxLine={3}>
                {modal?.services
                  ? Object.entries(modal?.services)
                      ?.filter(([key, value]) => value)
                      ?.map(([key, value]) => SERVICES[key])
                      .join(', ')
                  : '-'}
              </Text>
            </Flex>
          </Flex>
        </Content>
      </ModalLayout>
    </>
  );
};

export default FindStorePresenter;
